import React, { useState, useEffect, useCallback } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { toServerDate } from '../../../timezone';

const API_BASE_URL = '/api';

const EmailSettingsPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const notification = useNotification();

  // Form state
  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'VerOps',
    useTls: true,
    useSsl: false,
    enabled: false,
    connectionTimeout: 10000,
    readTimeout: 10000,
    appBaseUrl: '',
    invitationSubject: "You've been invited to VerOps",
    passwordResetSubject: 'Reset your VerOps password',
    invitationTokenExpiryHours: 48,
    passwordResetTokenExpiryHours: 24,
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/settings/email`, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email configuration');
      }

      const data = await response.json();
      setConfig(data);
      setFormData({
        smtpHost: data.smtpHost || '',
        smtpPort: data.smtpPort || 587,
        smtpUsername: data.smtpUsername || '',
        smtpPassword: '', // Never pre-fill password
        fromEmail: data.fromEmail || '',
        fromName: data.fromName || 'VerOps',
        useTls: data.useTls ?? true,
        useSsl: data.useSsl ?? false,
        enabled: data.enabled ?? false,
        connectionTimeout: data.connectionTimeout || 10000,
        readTimeout: data.readTimeout || 10000,
        appBaseUrl: data.appBaseUrl || window.location.origin,
        invitationSubject: data.invitationSubject || "You've been invited to VerOps",
        passwordResetSubject: data.passwordResetSubject || 'Reset your VerOps password',
        invitationTokenExpiryHours: data.invitationTokenExpiryHours || 48,
        passwordResetTokenExpiryHours: data.passwordResetTokenExpiryHours || 24,
      });
    } catch (error) {
      console.error('Failed to fetch email configuration:', error);
      notification.error('Failed to fetch email configuration');
    } finally {
      setLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare payload - only include password if it was changed
      const payload = { ...formData };
      if (!payload.smtpPassword) {
        delete payload.smtpPassword;
      }

      const response = await fetch(`${API_BASE_URL}/settings/email`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save email configuration');
      }

      const data = await response.json();
      setConfig(data);
      notification.success('Email configuration saved successfully');
    } catch (error) {
      console.error('Failed to save email configuration:', error);
      notification.error('Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      notification.error('Please enter a test email address');
      return;
    }

    try {
      setTesting(true);
      const response = await fetch(`${API_BASE_URL}/settings/email/test`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: testEmail }),
      });

      const data = await response.json();

      if (data.success) {
        notification.success('Test email sent successfully');
        setConfig(data.configuration);
        setTestDialogOpen(false);
      } else {
        notification.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to test email configuration:', error);
      notification.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>
      </Box>
    );
  }

  const isConfigured = formData.smtpHost && formData.smtpPort && formData.fromEmail;

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <EmailIcon />
            <Typography variant="h4" fontWeight="600">
              Email Settings
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Configure system email for invitations and password resets
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfig}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {/* Status Banner */}
      <Alert
        severity={config?.ready ? 'success' : config?.verified ? 'info' : 'warning'}
        sx={{ mb: 3 }}
        icon={config?.ready ? <CheckIcon /> : config?.verified ? <EmailIcon /> : <SettingsIcon />}
      >
        {config?.ready
          ? 'Email service is configured, enabled, and verified. Ready to send emails.'
          : config?.verified
          ? 'Email configuration is verified but not enabled. Enable it to start sending emails.'
          : config?.configured
          ? 'Email is configured but not verified. Send a test email to verify the configuration.'
          : 'Email is not configured. Fill in the SMTP settings below to enable email functionality.'}
      </Alert>

      <Grid container spacing={3}>
        {/* SMTP Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6">SMTP Configuration</Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  label="SMTP Host"
                  value={formData.smtpHost}
                  onChange={(e) => handleChange('smtpHost', e.target.value)}
                  placeholder="smtp.gmail.com"
                  fullWidth
                  helperText="The SMTP server hostname"
                />

                <TextField
                  label="SMTP Port"
                  type="number"
                  value={formData.smtpPort}
                  onChange={(e) => handleChange('smtpPort', parseInt(e.target.value) || 587)}
                  fullWidth
                  helperText="Common ports: 587 (TLS), 465 (SSL), 25 (unencrypted)"
                />

                <TextField
                  label="SMTP Username"
                  value={formData.smtpUsername}
                  onChange={(e) => handleChange('smtpUsername', e.target.value)}
                  fullWidth
                  helperText="Username for SMTP authentication (optional)"
                />

                <TextField
                  label="SMTP Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.smtpPassword}
                  onChange={(e) => handleChange('smtpPassword', e.target.value)}
                  fullWidth
                  placeholder={config?.hasPassword ? '••••••••' : 'Enter password'}
                  helperText={config?.hasPassword ? 'Leave empty to keep current password' : 'Password for SMTP authentication'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.useTls}
                        onChange={(e) => handleChange('useTls', e.target.checked)}
                      />
                    }
                    label="Use TLS (STARTTLS)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.useSsl}
                        onChange={(e) => handleChange('useSsl', e.target.checked)}
                      />
                    }
                    label="Use SSL"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Sender Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SendIcon color="primary" />
                <Typography variant="h6">Sender Settings</Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  label="From Email"
                  type="email"
                  value={formData.fromEmail}
                  onChange={(e) => handleChange('fromEmail', e.target.value)}
                  placeholder="noreply@example.com"
                  fullWidth
                  required
                  helperText="Email address that appears in the 'From' field"
                />

                <TextField
                  label="From Name"
                  value={formData.fromName}
                  onChange={(e) => handleChange('fromName', e.target.value)}
                  placeholder="VerOps"
                  fullWidth
                  helperText="Display name that appears in the 'From' field"
                />

                <TextField
                  label="Application Base URL"
                  value={formData.appBaseUrl}
                  onChange={(e) => handleChange('appBaseUrl', e.target.value)}
                  placeholder={window.location.origin}
                  fullWidth
                  helperText="Base URL used for links in emails (e.g., password reset links)"
                />

                <Box sx={{ pt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={(e) => handleChange('enabled', e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography>Email Service Enabled</Typography>
                        <Chip
                          size="small"
                          label={formData.enabled ? 'ON' : 'OFF'}
                          color={formData.enabled ? 'success' : 'default'}
                        />
                      </Stack>
                    }
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Email Templates */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="h6">Email Templates</Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  label="Invitation Email Subject"
                  value={formData.invitationSubject}
                  onChange={(e) => handleChange('invitationSubject', e.target.value)}
                  fullWidth
                  helperText="Subject line for user invitation emails"
                />

                <TextField
                  label="Password Reset Email Subject"
                  value={formData.passwordResetSubject}
                  onChange={(e) => handleChange('passwordResetSubject', e.target.value)}
                  fullWidth
                  helperText="Subject line for password reset emails"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Token Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6">Token Expiration</Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  label="Invitation Token Expiry (hours)"
                  type="number"
                  value={formData.invitationTokenExpiryHours}
                  onChange={(e) => handleChange('invitationTokenExpiryHours', parseInt(e.target.value) || 48)}
                  fullWidth
                  helperText="How long invitation links remain valid"
                  InputProps={{
                    inputProps: { min: 1, max: 168 },
                  }}
                />

                <TextField
                  label="Password Reset Token Expiry (hours)"
                  type="number"
                  value={formData.passwordResetTokenExpiryHours}
                  onChange={(e) => handleChange('passwordResetTokenExpiryHours', parseInt(e.target.value) || 24)}
                  fullWidth
                  helperText="How long password reset links remain valid"
                  InputProps={{
                    inputProps: { min: 1, max: 72 },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Connection Timeout (ms)"
                    type="number"
                    value={formData.connectionTimeout}
                    onChange={(e) => handleChange('connectionTimeout', parseInt(e.target.value) || 10000)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Read Timeout (ms)"
                    type="number"
                    value={formData.readTimeout}
                    onChange={(e) => handleChange('readTimeout', parseInt(e.target.value) || 10000)}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Test & Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">Test Configuration</Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Send a test email to verify your SMTP configuration is working correctly.
                    Make sure to save your changes before testing.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                  {config?.lastTestedAt && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">
                        Last tested: {toServerDate(config.lastTestedAt)?.toLocaleString()}
                      </Typography>
                      <Chip
                        size="small"
                        label={config.lastTestResult}
                        color={config.lastTestResult === 'SUCCESS' ? 'success' : 'error'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => setTestDialogOpen(true)}
                    disabled={!isConfigured}
                  >
                    Send Test Email
                  </Button>
                </Stack>
              </Stack>

              {config?.lastTestError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="500">Last Error:</Typography>
                  <Typography variant="body2">{config.lastTestError}</Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test Email Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter an email address to send a test email. This will verify that your SMTP
            configuration is working correctly.
          </Typography>
          <TextField
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            fullWidth
            autoFocus
            placeholder="test@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTest}
            variant="contained"
            disabled={testing || !testEmail}
            startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailSettingsPage;
